import { c as _c } from "react/compiler-runtime";
import { useQuery } from "react-query";
import { useApi } from "./useApi";
import { useActiveGithubOrgForUser, useActiveGithubOrgForUserNonSuspense } from "./useConnectedGithubOrgs";
import * as z from "zod";
import { useMemo } from "react";
import { getMonthlyBillingUsagePresentation } from "@/lib/monthlyBillingUsagePresentation";
const Usage = z.object({
  billable_minutes: z.number(),
  free_minutes: z.number()
});
const CoreUsage = z.object({
  vcpus: z.number(),
  jobs: z.number()
});
const CurrentCoreUsageResponse = z.object({
  current_usage: z.object({
    amd64: CoreUsage,
    arm64: CoreUsage,
    macos: CoreUsage
  }).nullable(),
  timestamp: z.string()
});
const CoreUsageTimeseriesResponse = z.object({
  timeseries: z.array(z.object({
    timestamp: z.string(),
    usage: z.object({
      amd64: CoreUsage,
      arm64: CoreUsage,
      macos: CoreUsage
    })
  }))
});
type CurrentCoreUsageResponse = z.infer<typeof CurrentCoreUsageResponse>;
type CoreUsageTimeseriesResponse = z.infer<typeof CoreUsageTimeseriesResponse>;
function formatUsageMonthDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Non-suspense: monthly weighted billable totals + allowance for Usage & Billing dashboards. */
export function useMonthlyUsageBillingSummary(enabled, t0) {
  const $ = _c(18);
  const selectedDate = t0 === undefined ? null : t0;
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  let t1;
  if ($[0] !== selectedDate) {
    t1 = selectedDate ?? new Date();
    $[0] = selectedDate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = formatUsageMonthDate(t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const date = t2;
  let t3;
  if ($[4] !== activeGithubOrg || $[5] !== date) {
    t3 = ["monthlyUsageBillingSummary", activeGithubOrg, date];
    $[4] = activeGithubOrg;
    $[5] = date;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== activeGithubOrg || $[8] !== api || $[9] !== date) {
    t4 = async () => {
      const response = await api.get(`user/github/orgs/${activeGithubOrg}/usage?date=${encodeURIComponent(date)}`);
      const usage = Usage.parse(response.data);
      const presentation = getMonthlyBillingUsagePresentation(usage.billable_minutes ?? 0, usage.free_minutes ?? 0);
      return {
        ...presentation,
        rawBillableWeighted: usage.billable_minutes ?? 0,
        rawFreeMinutes: usage.free_minutes ?? 0
      };
    };
    $[7] = activeGithubOrg;
    $[8] = api;
    $[9] = date;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = enabled && !!activeGithubOrg;
  let t6;
  if ($[11] !== t5) {
    t6 = {
      suspense: false,
      useErrorBoundary: false,
      enabled: t5,
      keepPreviousData: true,
      staleTime: 60000
    };
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const {
    data,
    isLoading,
    isError,
    isFetching
  } = useQuery(t3, t4, t6);
  const t7 = data ?? null;
  const t8 = enabled && !!activeGithubOrg ? isLoading : false;
  const t9 = enabled && !!activeGithubOrg ? isFetching : false;
  const t10 = enabled && !!activeGithubOrg ? isError : false;
  let t11;
  if ($[13] !== t10 || $[14] !== t7 || $[15] !== t8 || $[16] !== t9) {
    t11 = {
      summary: t7,
      isLoading: t8,
      isFetching: t9,
      isError: t10
    };
    $[13] = t10;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  return t11;
}
export function useCurrentCoreUsage() {
  const $ = _c(13);
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  let t0;
  if ($[0] !== activeGithubOrg) {
    t0 = ["currentCoreUsage", activeGithubOrg];
    $[0] = activeGithubOrg;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== activeGithubOrg || $[3] !== api) {
    t1 = async () => {
      if (!activeGithubOrg) {
        throw new Error("No active GitHub org");
      }
      const response = await api.get(`user/github/orgs/${activeGithubOrg}/metrics/core-usage/current`);
      const usage = CurrentCoreUsageResponse.parse(response.data);
      if (!usage.current_usage) {
        return null;
      }
      return {
        amd64: {
          vcpus: usage.current_usage.amd64.vcpus,
          jobs: usage.current_usage.amd64.jobs
        },
        arm64: {
          vcpus: usage.current_usage.arm64.vcpus,
          jobs: usage.current_usage.arm64.jobs
        },
        macos: {
          vcpus: usage.current_usage.macos.vcpus,
          jobs: usage.current_usage.macos.jobs
        },
        timestamp: new Date(usage.timestamp)
      };
    };
    $[2] = activeGithubOrg;
    $[3] = api;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = !!activeGithubOrg;
  let t3;
  if ($[5] !== t2) {
    t3 = {
      suspense: false,
      useErrorBoundary: false,
      refetchInterval: 60000,
      keepPreviousData: true,
      enabled: t2
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data,
    isLoading,
    error
  } = useQuery(t0, t1, t3);
  const t4 = data ?? undefined;
  const t5 = data ? data.amd64.vcpus + data.arm64.vcpus + data.macos.vcpus : 0;
  const t6 = data ? data.amd64.jobs + data.arm64.jobs + data.macos.jobs : 0;
  let t7;
  if ($[7] !== error || $[8] !== isLoading || $[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = {
      currentUsage: t4,
      isLoading,
      error,
      totalVCpus: t5,
      totalJobs: t6
    };
    $[7] = error;
    $[8] = isLoading;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
export function useCoreUsageTimeseries(t0, startDate, endDate) {
  const $ = _c(34);
  const windowSizeMinutes = t0 === undefined ? 15 : t0;
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const {
    currentUsage
  } = useCurrentCoreUsage();
  let t1;
  if ($[0] !== activeGithubOrg || $[1] !== endDate || $[2] !== startDate || $[3] !== windowSizeMinutes) {
    t1 = ["coreUsageTimeseries", activeGithubOrg, windowSizeMinutes, startDate, endDate];
    $[0] = activeGithubOrg;
    $[1] = endDate;
    $[2] = startDate;
    $[3] = windowSizeMinutes;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== activeGithubOrg || $[6] !== api || $[7] !== endDate || $[8] !== startDate || $[9] !== windowSizeMinutes) {
    t2 = async () => {
      const queryParams = [`window_size=${windowSizeMinutes}`, startDate ? `start_date=${startDate.toISOString()}` : null, endDate ? `end_date=${endDate.toISOString()}` : null].filter(Boolean).join("&");
      const response = await api.get(`user/github/orgs/${activeGithubOrg}/metrics/core-usage/timeseries?${queryParams}`);
      const usage = CoreUsageTimeseriesResponse.parse(response.data);
      const dataPoints = usage.timeseries.map(_temp);
      if (dataPoints.length === 0) {
        return [];
      }
      const dataPointsMap = new Map(dataPoints.map(_temp2));
      const filledDataPoints = [];
      const firstTimestamp = dataPoints[0].timestamp;
      const lastTimestamp = dataPoints[dataPoints.length - 1].timestamp;
      let currentTimestamp = new Date(firstTimestamp);
      while (currentTimestamp <= lastTimestamp) {
        const currentTime = currentTimestamp.getTime();
        const existingPoint = dataPointsMap.get(currentTime);
        if (existingPoint) {
          filledDataPoints.push(existingPoint);
        } else {
          filledDataPoints.push({
            timestamp: new Date(currentTimestamp),
            total: {
              vcpus: 0,
              jobs: 0
            },
            amd64: {
              vcpus: 0,
              jobs: 0
            },
            arm64: {
              vcpus: 0,
              jobs: 0
            },
            macos: {
              vcpus: 0,
              jobs: 0
            }
          });
        }
        currentTimestamp = new Date(currentTime + windowSizeMinutes * 60 * 1000);
      }
      return filledDataPoints;
    };
    $[5] = activeGithubOrg;
    $[6] = api;
    $[7] = endDate;
    $[8] = startDate;
    $[9] = windowSizeMinutes;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const t3 = !!activeGithubOrg;
  let t4;
  if ($[11] !== t3) {
    t4 = {
      suspense: false,
      useErrorBoundary: false,
      keepPreviousData: true,
      refetchInterval: 60000,
      enabled: t3
    };
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const {
    data,
    isLoading,
    error,
    isFetching
  } = useQuery(t1, t2, t4);
  let t5;
  if ($[13] !== currentUsage || $[14] !== data || $[15] !== windowSizeMinutes) {
    bb0: {
      if (!data || !currentUsage) {
        let t6;
        if ($[17] !== data) {
          t6 = data ?? [];
          $[17] = data;
          $[18] = t6;
        } else {
          t6 = $[18];
        }
        t5 = t6;
        break bb0;
      }
      const timeseriesData = [...data];
      const lastPoint = timeseriesData[timeseriesData.length - 1];
      const currentUsagePoint = {
        timestamp: currentUsage.timestamp,
        total: {
          vcpus: currentUsage.amd64.vcpus + currentUsage.arm64.vcpus + currentUsage.macos.vcpus,
          jobs: currentUsage.amd64.jobs + currentUsage.arm64.jobs + currentUsage.macos.jobs
        },
        amd64: {
          vcpus: currentUsage.amd64.vcpus,
          jobs: currentUsage.amd64.jobs
        },
        arm64: {
          vcpus: currentUsage.arm64.vcpus,
          jobs: currentUsage.arm64.jobs
        },
        macos: {
          vcpus: currentUsage.macos.vcpus,
          jobs: currentUsage.macos.jobs
        }
      };
      if (!lastPoint) {
        timeseriesData.push(currentUsagePoint);
        t5 = timeseriesData;
        break bb0;
      }
      const timeDiff = currentUsage.timestamp.getTime() - lastPoint.timestamp.getTime();
      const windowSizeMs = windowSizeMinutes * 60 * 1000;
      let result;
      if (timeDiff >= 0 && timeDiff < windowSizeMs) {
        let t6;
        if ($[19] !== timeseriesData) {
          t6 = timeseriesData.slice(0, -1);
          $[19] = timeseriesData;
          $[20] = t6;
        } else {
          t6 = $[20];
        }
        let t7;
        if ($[21] !== currentUsagePoint || $[22] !== t6) {
          t7 = [...t6, currentUsagePoint];
          $[21] = currentUsagePoint;
          $[22] = t6;
          $[23] = t7;
        } else {
          t7 = $[23];
        }
        result = t7;
      } else {
        if (currentUsage.timestamp > lastPoint.timestamp) {
          let t6;
          if ($[24] !== currentUsagePoint || $[25] !== timeseriesData) {
            t6 = [...timeseriesData, currentUsagePoint];
            $[24] = currentUsagePoint;
            $[25] = timeseriesData;
            $[26] = t6;
          } else {
            t6 = $[26];
          }
          result = t6;
        } else {
          let t6;
          if ($[27] !== timeseriesData) {
            t6 = [...timeseriesData];
            $[27] = timeseriesData;
            $[28] = t6;
          } else {
            t6 = $[28];
          }
          result = t6;
        }
      }
      t5 = result;
    }
    $[13] = currentUsage;
    $[14] = data;
    $[15] = windowSizeMinutes;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const combinedData = t5;
  let t6;
  if ($[29] !== combinedData || $[30] !== error || $[31] !== isFetching || $[32] !== isLoading) {
    t6 = {
      usageSnapshots: combinedData,
      isLoading,
      isFetching,
      error
    };
    $[29] = combinedData;
    $[30] = error;
    $[31] = isFetching;
    $[32] = isLoading;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  return t6;
}
function _temp2(point_0) {
  return [point_0.timestamp.getTime(), point_0];
}
function _temp(point) {
  return {
    timestamp: new Date(point.timestamp),
    total: {
      vcpus: point.usage.amd64.vcpus + point.usage.arm64.vcpus + point.usage.macos.vcpus,
      jobs: point.usage.amd64.jobs + point.usage.arm64.jobs + point.usage.macos.jobs
    },
    amd64: {
      vcpus: point.usage.amd64.vcpus,
      jobs: point.usage.amd64.jobs
    },
    arm64: {
      vcpus: point.usage.arm64.vcpus,
      jobs: point.usage.arm64.jobs
    },
    macos: {
      vcpus: point.usage.macos.vcpus,
      jobs: point.usage.macos.jobs
    }
  };
}
export function useCurrentCoreUsageNonSuspense() {
  const $ = _c(13);
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUserNonSuspense();
  let t0;
  if ($[0] !== activeGithubOrg) {
    t0 = ["currentCoreUsage", activeGithubOrg];
    $[0] = activeGithubOrg;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== activeGithubOrg || $[3] !== api) {
    t1 = async () => {
      if (!activeGithubOrg) {
        throw new Error("No active GitHub org");
      }
      const response = await api.get(`user/github/orgs/${activeGithubOrg}/metrics/core-usage/current`);
      const usage = CurrentCoreUsageResponse.parse(response.data);
      if (!usage.current_usage) {
        return null;
      }
      return {
        amd64: {
          vcpus: usage.current_usage.amd64.vcpus,
          jobs: usage.current_usage.amd64.jobs
        },
        arm64: {
          vcpus: usage.current_usage.arm64.vcpus,
          jobs: usage.current_usage.arm64.jobs
        },
        macos: {
          vcpus: usage.current_usage.macos.vcpus,
          jobs: usage.current_usage.macos.jobs
        },
        timestamp: new Date(usage.timestamp)
      };
    };
    $[2] = activeGithubOrg;
    $[3] = api;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = !!activeGithubOrg;
  let t3;
  if ($[5] !== t2) {
    t3 = {
      suspense: false,
      useErrorBoundary: false,
      refetchInterval: 60000,
      keepPreviousData: true,
      enabled: t2
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data,
    isLoading,
    error
  } = useQuery(t0, t1, t3);
  const t4 = data ?? undefined;
  const t5 = data ? data.amd64.vcpus + data.arm64.vcpus + data.macos.vcpus : 0;
  const t6 = data ? data.amd64.jobs + data.arm64.jobs + data.macos.jobs : 0;
  let t7;
  if ($[7] !== error || $[8] !== isLoading || $[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = {
      currentUsage: t4,
      isLoading,
      error,
      totalVCpus: t5,
      totalJobs: t6
    };
    $[7] = error;
    $[8] = isLoading;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}

/**
 * Builds a core-usage timeseries window whose `end` is floored to the nearest
 * `windowSizeMinutes` boundary in UTC (seconds and ms zeroed), with
 * `start = end - spanHours`. Quantizing the end keeps the react-query key
 * derived from these dates stable across navigations within the same bucket, so
 * remounting a widget that requests the same logical window reuses the cache
 * instead of refetching; two widgets that ask for the same (org, windowSize,
 * span) collide on one cache entry and one poll.
 * e.g. windowSizeMinutes=15 at 14:07:33 UTC → end 14:00:00, start 24h prior.
 */
export function getCoreUsageWindow(windowSizeMinutes: number, spanHours: number): {
  startDate: Date;
  endDate: Date;
} {
  const now = new Date();
  const flooredMinutes = Math.floor(now.getUTCMinutes() / windowSizeMinutes) * windowSizeMinutes;
  const endDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours(), flooredMinutes, 0, 0));
  const startDate = new Date(endDate);
  startDate.setUTCHours(startDate.getUTCHours() - spanHours);
  return {
    startDate,
    endDate
  };
}
export function useCoreUsageTimeseriesNonSuspense(t0, startDate, endDate) {
  const $ = _c(34);
  const windowSizeMinutes = t0 === undefined ? 15 : t0;
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUserNonSuspense();
  const {
    currentUsage
  } = useCurrentCoreUsageNonSuspense();
  let t1;
  if ($[0] !== activeGithubOrg || $[1] !== endDate || $[2] !== startDate || $[3] !== windowSizeMinutes) {
    t1 = ["coreUsageTimeseries", activeGithubOrg, windowSizeMinutes, startDate, endDate];
    $[0] = activeGithubOrg;
    $[1] = endDate;
    $[2] = startDate;
    $[3] = windowSizeMinutes;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== activeGithubOrg || $[6] !== api || $[7] !== endDate || $[8] !== startDate || $[9] !== windowSizeMinutes) {
    t2 = async () => {
      const queryParams = [`window_size=${windowSizeMinutes}`, startDate ? `start_date=${startDate.toISOString()}` : null, endDate ? `end_date=${endDate.toISOString()}` : null].filter(Boolean).join("&");
      const response = await api.get(`user/github/orgs/${activeGithubOrg}/metrics/core-usage/timeseries?${queryParams}`);
      const usage = CoreUsageTimeseriesResponse.parse(response.data);
      const dataPoints = usage.timeseries.map(_temp3);
      if (dataPoints.length === 0) {
        return [];
      }
      const dataPointsMap = new Map(dataPoints.map(_temp4));
      const filledDataPoints = [];
      const firstTimestamp = dataPoints[0].timestamp;
      const lastTimestamp = dataPoints[dataPoints.length - 1].timestamp;
      let currentTimestamp = new Date(firstTimestamp);
      while (currentTimestamp <= lastTimestamp) {
        const currentTime = currentTimestamp.getTime();
        const existingPoint = dataPointsMap.get(currentTime);
        if (existingPoint) {
          filledDataPoints.push(existingPoint);
        } else {
          filledDataPoints.push({
            timestamp: new Date(currentTimestamp),
            total: {
              vcpus: 0,
              jobs: 0
            },
            amd64: {
              vcpus: 0,
              jobs: 0
            },
            arm64: {
              vcpus: 0,
              jobs: 0
            },
            macos: {
              vcpus: 0,
              jobs: 0
            }
          });
        }
        currentTimestamp = new Date(currentTime + windowSizeMinutes * 60 * 1000);
      }
      return filledDataPoints;
    };
    $[5] = activeGithubOrg;
    $[6] = api;
    $[7] = endDate;
    $[8] = startDate;
    $[9] = windowSizeMinutes;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const t3 = !!activeGithubOrg;
  let t4;
  if ($[11] !== t3) {
    t4 = {
      suspense: false,
      useErrorBoundary: false,
      keepPreviousData: true,
      refetchInterval: 60000,
      enabled: t3
    };
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const {
    data,
    isLoading,
    error,
    isFetching
  } = useQuery(t1, t2, t4);
  let t5;
  if ($[13] !== currentUsage || $[14] !== data || $[15] !== windowSizeMinutes) {
    bb0: {
      if (!data || !currentUsage) {
        let t6;
        if ($[17] !== data) {
          t6 = data ?? [];
          $[17] = data;
          $[18] = t6;
        } else {
          t6 = $[18];
        }
        t5 = t6;
        break bb0;
      }
      const timeseriesData = [...data];
      const lastPoint = timeseriesData[timeseriesData.length - 1];
      const currentUsagePoint = {
        timestamp: currentUsage.timestamp,
        total: {
          vcpus: currentUsage.amd64.vcpus + currentUsage.arm64.vcpus + currentUsage.macos.vcpus,
          jobs: currentUsage.amd64.jobs + currentUsage.arm64.jobs + currentUsage.macos.jobs
        },
        amd64: {
          vcpus: currentUsage.amd64.vcpus,
          jobs: currentUsage.amd64.jobs
        },
        arm64: {
          vcpus: currentUsage.arm64.vcpus,
          jobs: currentUsage.arm64.jobs
        },
        macos: {
          vcpus: currentUsage.macos.vcpus,
          jobs: currentUsage.macos.jobs
        }
      };
      if (!lastPoint) {
        timeseriesData.push(currentUsagePoint);
        t5 = timeseriesData;
        break bb0;
      }
      const timeDiff = currentUsage.timestamp.getTime() - lastPoint.timestamp.getTime();
      const windowSizeMs = windowSizeMinutes * 60 * 1000;
      let result;
      if (timeDiff >= 0 && timeDiff < windowSizeMs) {
        let t6;
        if ($[19] !== timeseriesData) {
          t6 = timeseriesData.slice(0, -1);
          $[19] = timeseriesData;
          $[20] = t6;
        } else {
          t6 = $[20];
        }
        let t7;
        if ($[21] !== currentUsagePoint || $[22] !== t6) {
          t7 = [...t6, currentUsagePoint];
          $[21] = currentUsagePoint;
          $[22] = t6;
          $[23] = t7;
        } else {
          t7 = $[23];
        }
        result = t7;
      } else {
        if (currentUsage.timestamp > lastPoint.timestamp) {
          let t6;
          if ($[24] !== currentUsagePoint || $[25] !== timeseriesData) {
            t6 = [...timeseriesData, currentUsagePoint];
            $[24] = currentUsagePoint;
            $[25] = timeseriesData;
            $[26] = t6;
          } else {
            t6 = $[26];
          }
          result = t6;
        } else {
          let t6;
          if ($[27] !== timeseriesData) {
            t6 = [...timeseriesData];
            $[27] = timeseriesData;
            $[28] = t6;
          } else {
            t6 = $[28];
          }
          result = t6;
        }
      }
      t5 = result;
    }
    $[13] = currentUsage;
    $[14] = data;
    $[15] = windowSizeMinutes;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const combinedData = t5;
  let t6;
  if ($[29] !== combinedData || $[30] !== error || $[31] !== isFetching || $[32] !== isLoading) {
    t6 = {
      usageSnapshots: combinedData,
      isLoading,
      isFetching,
      error
    };
    $[29] = combinedData;
    $[30] = error;
    $[31] = isFetching;
    $[32] = isLoading;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  return t6;
}
function _temp4(point_0) {
  return [point_0.timestamp.getTime(), point_0];
}
function _temp3(point) {
  return {
    timestamp: new Date(point.timestamp),
    total: {
      vcpus: point.usage.amd64.vcpus + point.usage.arm64.vcpus + point.usage.macos.vcpus,
      jobs: point.usage.amd64.jobs + point.usage.arm64.jobs + point.usage.macos.jobs
    },
    amd64: {
      vcpus: point.usage.amd64.vcpus,
      jobs: point.usage.amd64.jobs
    },
    arm64: {
      vcpus: point.usage.arm64.vcpus,
      jobs: point.usage.arm64.jobs
    },
    macos: {
      vcpus: point.usage.macos.vcpus,
      jobs: point.usage.macos.jobs
    }
  };
}